import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SeekerAuthProvider } from "@/context/SeekerAuthContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { FeaturesProvider } from "@/context/FeaturesContext";
import { Toaster } from "@/components/ui/sonner";
import { AIHealthProvider } from "@/components/AIHealthBanner";

import Landing from "@/pages/Landing";
import ManagedRecruitment from "@/pages/ManagedRecruitment";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Rescue from "@/pages/Rescue";
import Dashboard from "@/pages/Dashboard";
import JobsList from "@/pages/JobsList";
import JobCreate from "@/pages/JobCreate";
import JobView from "@/pages/JobView";
import CandidatesList from "@/pages/CandidatesList";
import CandidateDetail from "@/pages/CandidateDetail";
import CompanyPage from "@/pages/CompanyPage";
import AdminPanel from "@/pages/AdminPanel";
import AdminEmployers from "@/pages/AdminEmployers";
import AdminCampaignCodes from "@/pages/AdminCampaignCodes";
import AdminSubscriptionPlans from "@/pages/AdminSubscriptionPlans";
import OnboardingPlans from "@/pages/OnboardingPlans";
import AdminLandingContent from "@/pages/AdminLandingContent";
import OfferSettings from "@/pages/OfferSettings";
import ScreeningPreferences from "@/pages/ScreeningPreferences";
import DocFreshnessSettings from "@/pages/DocFreshnessSettings";
import DocsPending from "@/pages/DocsPending";
import PortalDocUpload from "@/pages/PortalDocUpload";
import AdminIntegrationKeys from "@/pages/AdminIntegrationKeys";
import SpotlightRoi from "@/pages/SpotlightRoi";
import OffersForCandidate from "@/pages/OffersForCandidate";
import OffersInbox from "@/pages/OffersInbox";
import HiringListsPage from "@/pages/HiringListsPage";
import HiringListDetail from "@/pages/HiringListDetail";
import JobEdit from "@/pages/JobEdit";
import PublicJobApplyRoute from "@/pages/PublicJobApplyRoute";
import PublicShortlistView from "@/pages/PublicShortlistView";
import PublicTalentPool from "@/pages/PublicTalentPool";
import RecruiterPerformance from "@/pages/RecruiterPerformance";
import CalendarPage from "@/pages/CalendarPage";
import CombinedPipeline from "@/pages/CombinedPipeline";
import BillingPlans from "@/pages/BillingPlans";
import InterviewerDashboard from "@/pages/InterviewerDashboard";
import InterviewerDay from "@/pages/InterviewerDay";
import InterviewSession from "@/pages/InterviewSession";
import AppShell from "@/components/AppShell";

// Seeker portal
import SeekerShell from "@/components/SeekerShell";
import { SeekerLogin, SeekerSignup } from "@/pages/seeker/SeekerAuth";
import SeekerJobs from "@/pages/seeker/SeekerJobs";
import SeekerApplications from "@/pages/seeker/SeekerApplications";
import SeekerInterviews from "@/pages/seeker/SeekerInterviews";
import SeekerMessages from "@/pages/seeker/SeekerMessages";
import SeekerProfile from "@/pages/seeker/SeekerProfile";
import SeekerClaim from "@/pages/seeker/SeekerClaim";
import SeekerSavedJobs from "@/pages/seeker/SeekerSavedJobs";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import EmbedBoard from "@/pages/EmbedBoard";
import EmbedBoardV2 from "@/pages/EmbedBoardV2";
import ClientsPage from "@/pages/ClientsPage";
import AnalyticsDashboard from "@/pages/AnalyticsDashboard";
import JobAnalytics from "@/pages/JobAnalytics";
import CandidateDuplicates from "@/pages/CandidateDuplicates";
import CompanyBranding from "@/pages/CompanyBranding";
import CompanyLandingEditor from "@/pages/CompanyLandingEditor";
import PublicCompanyLanding from "@/pages/PublicCompanyLanding";
import MockCheckout from "@/pages/MockCheckout";
import EmbedBoardsPage from "@/pages/EmbedBoardsPage";
import SeedsPage from "@/pages/SeedsPage";
import SkillCenter from "@/pages/SkillCenter";
import TalentSearch from "@/pages/TalentSearch";
import BulkJobCreate from "@/pages/BulkJobCreate";
import MyTickets from "@/pages/MyTickets";
import AdminHelpdesk from "@/pages/AdminHelpdesk";
import AdminSpotlightFunnel from "@/pages/AdminSpotlightFunnel";
import NotificationsPage from "@/pages/NotificationsPage";
import CompanyEmailTemplates from "@/pages/CompanyEmailTemplates";
import CompanyEmailSignature from "@/pages/CompanyEmailSignature";
import CompanyEmailSettings from "@/pages/CompanyEmailSettings";
import CompanyInteraktSettings from "@/pages/CompanyInteraktSettings";
import CompanyInterviewerSettings from "@/pages/CompanyInterviewerSettings";
import Feed from "@/pages/Feed";
import InboxUnmatched from "@/pages/InboxUnmatched";
import RecruiterMessages from "@/pages/RecruiterMessages";
import HiresLedger from "@/pages/HiresLedger";
import InterviewHistoryPage from "@/pages/InterviewHistoryPage";
import CostsPage from "@/pages/CostsPage";
import ReportsHub from "@/pages/ReportsHub";
import MyWeek from "@/pages/MyWeek";
import AIFeedback from "@/pages/AIFeedback";
import SpotlightPage from "@/pages/SpotlightPage";
import SpotlightCampaigns from "@/pages/SpotlightCampaigns";
import SpotlightDirectory from "@/pages/SpotlightDirectory";
import PricingSpotlight from "@/pages/PricingSpotlight";
import PublicPricing from "@/pages/PublicPricing";
import DailyStatusReport from "@/pages/DailyStatusReport";
import FeatureManagement from "@/pages/FeatureManagement";
import AdminSubscriptions from "@/pages/AdminSubscriptions";
import AdminHub from "@/pages/AdminHub";
import AdminSellerBilling from "@/pages/AdminSellerBilling";
import Billing from "@/pages/Billing";
import Learn from "@/pages/Learn";

function Protected({ children, roles }) {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-sm text-muted-foreground font-mono">loading...</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    // Seekers must use their own portal
    if (user.role === "seeker") return <Navigate to="/seeker/jobs" replace />;
    // Interviewers are restricted to their own dashboard
    if (user.role === "interviewer" && !roles?.includes("interviewer")) return <Navigate to="/interviewer" replace />;
    if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
    // Fresh employer signups must clear the plan-selection onboarding
    // step before they see the main app. `_company.onboarding_status` is
    // set to "plans_pending" by /auth/register and cleared by
    // /onboarding/complete (or the checkout webhook).
    if (
        user.role === "employer"
        && user?._company?.onboarding_status === "plans_pending"
        && typeof window !== "undefined"
        && !window.location.pathname.startsWith("/onboarding")
    ) {
        return <Navigate to="/onboarding/plans" replace />;
    }
    return <AppShell>{children}</AppShell>;
}

// Embed-mode wrapper — same auth gate as Protected but renders the page
// without AppShell nav. Used by the ResumePreviewDialog split-view pane.
function ProtectedEmbed({ children, roles }) {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-sm text-muted-foreground font-mono">loading...</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    if (user.role === "seeker") return <Navigate to="/seeker/jobs" replace />;
    if (user.role === "interviewer" && !roles?.includes("interviewer")) return <Navigate to="/interviewer" replace />;
    if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
    return <div className="min-h-screen bg-background">{children}</div>;
}

// Lean wrapper for the Interview focus-mode pages: SAME auth gate as Protected
// but renders a standalone layout (no AppShell, no recruiter navigation). Any
// authenticated company user (interviewer, recruiter, employer, admin) can
// enter this view — they'll see the subset of interviews they're assigned to.
function InterviewerOnly({ children }) {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="text-sm text-muted-foreground font-mono">loading...</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    if (!["interviewer", "recruiter", "employer", "admin"].includes(user.role)) {
        return <Navigate to="/dashboard" replace />;
    }
    return children;
}

function App() {
    return (
        <BrandingProvider>
            <AuthProvider>
                <FeaturesProvider>
                <SeekerAuthProvider>
                    <AIHealthProvider>
                    <BrowserRouter>
                        <Routes>
                            <Route path="/" element={<Landing />} />
                            <Route path="/managed-recruitment" element={<ManagedRecruitment />} />
                            <Route path="/login" element={<Login />} />
                            <Route path="/signup" element={<Signup />} />
                            <Route path="/rescue" element={<Rescue />} />
                            <Route path="/forgot-password" element={<ForgotPassword />} />
                            <Route path="/reset-password/:token" element={<ResetPassword />} />
                            <Route path="/seeker/reset-password/:token" element={<ResetPassword />} />
                            <Route path="/j/:slug" element={<PublicJobApplyRoute />} />
                            <Route path="/shortlist/:token" element={<PublicShortlistView />} />
                            <Route path="/spotlight" element={<SpotlightDirectory />} />
                            <Route path="/spotlight/:companySlug/:campaignSlug" element={<SpotlightPage />} />
                            <Route path="/pricing/spotlight" element={<PricingSpotlight />} />
                            <Route path="/pricing" element={<PublicPricing />} />
                            <Route path="/p/:slug" element={<PublicTalentPool />} />
                            <Route path="/u/:slug" element={<PublicCompanyLanding />} />
                            <Route path="/billing/mock-checkout/:checkoutId" element={<Protected roles={["employer"]}><MockCheckout /></Protected>} />

                            {/* Seeker portal */}
                            <Route path="/seeker/login" element={<SeekerLogin />} />
                            <Route path="/seeker/signup" element={<SeekerSignup />} />
                            <Route path="/seeker/claim/:token" element={<SeekerClaim />} />
                            <Route path="/portal/upload/:token" element={<PortalDocUpload />} />
                            <Route path="/seeker/jobs" element={<SeekerShell><SeekerJobs /></SeekerShell>} />
                            <Route path="/seeker/applications" element={<SeekerShell><SeekerApplications /></SeekerShell>} />
                            <Route path="/seeker/interviews" element={<SeekerShell><SeekerInterviews /></SeekerShell>} />
                            <Route path="/seeker/messages" element={<SeekerShell><SeekerMessages /></SeekerShell>} />
                            <Route path="/seeker/messages/:threadId" element={<SeekerShell><SeekerMessages /></SeekerShell>} />
                            <Route path="/seeker/profile" element={<SeekerShell><SeekerProfile /></SeekerShell>} />
                            <Route path="/seeker/saved" element={<SeekerShell><SeekerSavedJobs /></SeekerShell>} />
                            <Route path="/seeker" element={<Navigate to="/seeker/jobs" replace />} />

                            {/* Public embed (no shell, iframe-friendly) */}
                            <Route path="/embed/board/:slug" element={<EmbedBoard />} />
                            <Route path="/embed/board-v2/:boardId" element={<EmbedBoardV2 />} />

                            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
                            <Route path="/feed" element={<Protected roles={["employer","recruiter"]}><Feed /></Protected>} />
                            <Route path="/inbox" element={<Protected roles={["employer","recruiter"]}><InboxUnmatched /></Protected>} />
                            <Route path="/messages" element={<Protected roles={["employer","recruiter"]}><RecruiterMessages /></Protected>} />
                            <Route path="/messages/:threadId" element={<Protected roles={["employer","recruiter"]}><RecruiterMessages /></Protected>} />
                            <Route path="/hires" element={<Protected roles={["employer","recruiter"]}><HiresLedger /></Protected>} />
                            <Route path="/interviews/history" element={<Protected roles={["employer","recruiter"]}><InterviewHistoryPage /></Protected>} />
                            <Route path="/my-week" element={<Protected roles={["employer","recruiter"]}><MyWeek /></Protected>} />
                            <Route path="/costs" element={<Protected roles={["employer","recruiter"]}><CostsPage /></Protected>} />
                            <Route path="/reports" element={<Protected roles={["employer","recruiter"]}><ReportsHub /></Protected>} />
                            <Route path="/reports/dsr" element={<Protected roles={["employer","recruiter"]}><DailyStatusReport /></Protected>} />
                            <Route path="/admin/features" element={<Protected roles={["admin"]}><FeatureManagement /></Protected>} />
                            <Route path="/admin/subscriptions" element={<Protected roles={["admin"]}><AdminSubscriptions /></Protected>} />
                            <Route path="/admin/employers" element={<Protected roles={["admin"]}><AdminEmployers /></Protected>} />
                            <Route path="/admin/campaign-codes" element={<Protected roles={["admin"]}><AdminCampaignCodes /></Protected>} />
                            <Route path="/admin/subscription-plans" element={<Protected roles={["admin"]}><AdminSubscriptionPlans /></Protected>} />
                            <Route path="/admin/integration-keys" element={<Protected roles={["admin"]}><AdminIntegrationKeys /></Protected>} />
                            <Route path="/spotlight-roi" element={<Protected roles={["employer","admin"]}><SpotlightRoi /></Protected>} />
                            <Route path="/onboarding/plans" element={<Protected roles={["employer"]}><OnboardingPlans /></Protected>} />
                            <Route path="/learn" element={<Protected roles={["employer","recruiter","admin"]}><Learn /></Protected>} />
                            <Route path="/jobs" element={<Protected><JobsList /></Protected>} />
                            <Route path="/jobs/bulk-create" element={<Protected roles={["employer","recruiter"]}><BulkJobCreate /></Protected>} />
                            <Route path="/clients" element={<Protected roles={["employer","recruiter"]}><ClientsPage /></Protected>} />
                            <Route path="/analytics" element={<Protected roles={["employer","recruiter"]}><AnalyticsDashboard /></Protected>} />
                            <Route path="/job-analytics" element={<Protected roles={["employer","recruiter"]}><JobAnalytics /></Protected>} />
                            <Route path="/candidate-duplicates" element={<Protected roles={["employer","recruiter"]}><CandidateDuplicates /></Protected>} />
                            <Route path="/branding" element={<Protected roles={["employer","admin"]}><CompanyBranding /></Protected>} />
                            <Route path="/ai-feedback" element={<Protected roles={["employer","recruiter","admin"]}><AIFeedback /></Protected>} />
                            <Route path="/campaigns" element={<Protected roles={["employer","recruiter","admin"]}><SpotlightCampaigns /></Protected>} />
                            <Route path="/landing-page" element={<Protected roles={["employer","admin"]}><CompanyLandingEditor /></Protected>} />
                            <Route path="/embed-boards" element={<Protected roles={["employer","recruiter"]}><EmbedBoardsPage /></Protected>} />
                            <Route path="/seeds" element={<Protected roles={["employer","recruiter"]}><SeedsPage /></Protected>} />
                            <Route path="/skill-center" element={<Protected roles={["employer","recruiter","admin"]}><SkillCenter /></Protected>} />
                            <Route path="/talent-search" element={<Protected roles={["employer","recruiter"]}><TalentSearch /></Protected>} />
                            <Route path="/talent-search/:cid" element={<Protected roles={["employer","recruiter"]}><TalentSearch /></Protected>} />
                            <Route path="/jobs/new" element={<Protected roles={["employer","recruiter"]}><JobCreate /></Protected>} />
                            <Route path="/jobs/:id/edit" element={<Protected roles={["employer","recruiter"]}><JobEdit /></Protected>} />
                            <Route path="/jobs/:id" element={<Protected><JobView /></Protected>} />
                            <Route path="/candidates" element={<Protected><CandidatesList /></Protected>} />
                            <Route path="/candidates/:id" element={<Protected roles={["employer","recruiter"]}><CandidateDetail /></Protected>} />
                            <Route path="/candidates/:id/embed" element={<ProtectedEmbed roles={["employer","recruiter"]}><CandidateDetail /></ProtectedEmbed>} />
                            <Route path="/lists" element={<Protected roles={["employer","recruiter"]}><HiringListsPage /></Protected>} />
                            <Route path="/lists/:id" element={<Protected roles={["employer","recruiter"]}><HiringListDetail /></Protected>} />
                            <Route path="/team" element={<Protected roles={["employer","recruiter"]}><RecruiterPerformance /></Protected>} />
                            <Route path="/calendar" element={<Protected roles={["employer","recruiter"]}><CalendarPage /></Protected>} />
                            <Route path="/pipeline" element={<Protected roles={["employer","recruiter"]}><CombinedPipeline /></Protected>} />
                            <Route path="/billing/plans" element={<Protected roles={["employer","recruiter"]}><BillingPlans /></Protected>} />
                            <Route path="/interviewer" element={<InterviewerOnly><InterviewerDashboard /></InterviewerOnly>} />
                            <Route path="/interviewer/day" element={<InterviewerOnly><InterviewerDay /></InterviewerOnly>} />
                            <Route path="/interviewer/interview/:iid" element={<InterviewerOnly><InterviewSession /></InterviewerOnly>} />
                            <Route path="/company" element={<Protected roles={["employer","recruiter"]}><CompanyPage /></Protected>} />
                            <Route path="/admin" element={<Protected roles={["admin"]}><AdminHub /></Protected>} />
                            <Route path="/admin/legacy" element={<Protected roles={["admin"]}><AdminPanel /></Protected>} />
                            <Route path="/admin/seller-billing" element={<Protected roles={["admin"]}><AdminSellerBilling /></Protected>} />
                            <Route path="/billing" element={<Protected roles={["employer","recruiter"]}><Billing /></Protected>} />
                            <Route path="/admin/landing-content" element={<Protected roles={["admin"]}><AdminLandingContent /></Protected>} />
                            <Route path="/company/email-templates" element={<Protected roles={["employer","recruiter"]}><CompanyEmailTemplates /></Protected>} />
                            <Route path="/settings/email-templates" element={<Protected roles={["employer","recruiter"]}><CompanyEmailTemplates /></Protected>} />
                            <Route path="/company/email-signature" element={<Protected roles={["employer","recruiter"]}><CompanyEmailSignature /></Protected>} />
                            <Route path="/settings/email-signature" element={<Protected roles={["employer","recruiter"]}><CompanyEmailSignature /></Protected>} />
                            <Route path="/settings/email" element={<Protected roles={["employer","recruiter","admin"]}><CompanyEmailSettings /></Protected>} />
                            <Route path="/company/whatsapp" element={<Protected roles={["employer","recruiter","admin"]}><CompanyInteraktSettings /></Protected>} />
                            <Route path="/settings/whatsapp" element={<Protected roles={["employer","recruiter","admin"]}><CompanyInteraktSettings /></Protected>} />
                            <Route path="/company/interviewer-settings" element={<Protected roles={["employer","admin"]}><CompanyInterviewerSettings /></Protected>} />
                            <Route path="/company/offer-settings" element={<Protected roles={["employer","admin"]}><OfferSettings /></Protected>} />
                            <Route path="/company/screening-preferences" element={<Protected roles={["employer","admin"]}><ScreeningPreferences /></Protected>} />
                            <Route path="/company/doc-freshness" element={<Protected roles={["employer","admin"]}><DocFreshnessSettings /></Protected>} />
                            <Route path="/docs-pending" element={<Protected roles={["employer","recruiter","admin"]}><DocsPending /></Protected>} />
                            <Route path="/candidates/:id/offers" element={<Protected roles={["employer","recruiter"]}><OffersForCandidate /></Protected>} />
                            <Route path="/offers/inbox" element={<Protected roles={["employer","admin"]}><OffersInbox /></Protected>} />
                            <Route path="/tickets" element={<Protected><MyTickets /></Protected>} />
                            <Route path="/admin/helpdesk" element={<Protected roles={["admin"]}><AdminHelpdesk /></Protected>} />
                            <Route path="/admin/spotlight-funnel" element={<Protected roles={["admin"]}><AdminSpotlightFunnel /></Protected>} />
                            <Route path="/notifications" element={<Protected><NotificationsPage /></Protected>} />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </BrowserRouter>
                    <Toaster position="bottom-right" richColors />
                    </AIHealthProvider>
                </SeekerAuthProvider>
                </FeaturesProvider>
            </AuthProvider>
        </BrandingProvider>
    );
}

export default App;
